/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Andrew Merrill <andrew.merrill@deliciousai.com>
 */

import { ApolloClient, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { addOperationNameToUrl } from '@dai/common'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'

import cache from './cache'
import { auth } from './Firebase'

const { VITE_APP__MERCURY_API_URL, VITE_APP__ML_ULTRA_API_URL, DEV } =
  import.meta.env

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations,
        )}, Path: ${path}`,
      )
    })
  if (networkError) console.log(`[Network error]: ${networkError}`)
})

const getServiceUrl = (endpoint: string) => {
  switch (endpoint) {
    case 'mlUltra':
      return VITE_APP__ML_ULTRA_API_URL
    default:
      return VITE_APP__MERCURY_API_URL
  }
}

const uriLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext()
  const serviceUrl = getServiceUrl(context.endPoint)
  operation.setContext({
    ...context,
    uri: `${new URL(serviceUrl).origin}/graphql/`,
  })
  return forward(operation)
})

const authLink = setContext(async (_, context) => {
  try {
    const token = await auth.currentUser?.getIdToken()

    return {
      ...context,
      headers: {
        ...context.headers,
        ...(token && { authorization: `Bearer ${token}` }),
        TIMEZONE: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }
  } catch (error) {
    return context
  }
})

export const client = new ApolloClient({
  link: ApolloLink.from([
    uriLink,
    addOperationNameToUrl,
    ...(DEV ? [errorLink] : []),
    authLink,
    createUploadLink({ credentials: 'same-origin' }),
  ]),
  cache,
})
